import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  PageEditorShell,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

function pageValue(form) {
  return form?.catalog?.productsPage || {};
}

function cardValue(form) {
  return form?.catalog?.cards || {};
}

function headerBackground(page, theme) {
  if (page.headerBackgroundColor) return page.headerBackgroundColor;
  return `radial-gradient(circle at 15% 20%, ${theme?.primaryColor || "#E44E31"} 0%, transparent 34%), linear-gradient(125deg, ${theme?.secondaryColor || "#494A98"}, #17182d)`;
}

function ProductsPagePreview({ form }) {
  const page = pageValue(form);
  const cards = cardValue(form);
  const theme = form?.theme || {};
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const headerText = page.headerTextColor || "#FFFFFF";
  const cardBackground = cards.backgroundColor || surface;

  return (
    <Box sx={{ minHeight: 680, bgcolor: background, color: textColor }}>
      <Box sx={{ px: { xs: 2, md: 5 }, pt: { xs: 7, md: 9 }, pb: 5, color: headerText, background: headerBackground(page, theme) }}>
        {page.eyebrow ? (
          <Typography variant="overline" sx={{ color: "inherit", opacity: 0.72 }}>
            {page.eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h3" sx={{ color: "inherit", fontWeight: 900 }}>
          {page.title || form?.navigation?.productsLabel || "Productos y servicios"}
        </Typography>
        {page.subtitle ? (
          <Typography sx={{ mt: 1, color: "inherit", opacity: 0.82 }}>{page.subtitle}</Typography>
        ) : null}
        <Typography sx={{ mt: 1, color: "inherit", opacity: 0.72 }}>
          12 {page.resultPlural || "resultados"}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 4 }, pb: 5 }}>
        {page.showSearch !== false ? (
          <Box sx={{ mt: -2.5, p: 1.4, bgcolor: surface, borderRadius: 2.5, boxShadow: "0 14px 36px rgba(25,36,47,.14)" }}>
            <TextField
              fullWidth
              size="small"
              disabled
              placeholder={page.searchPlaceholder || "Buscar por nombre, servicio, código, categoría o subcategoría"}
              InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: mutedText }} /> }}
            />
          </Box>
        ) : null}

        <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: page.showFilters === false ? "1fr" : { xs: "1fr", md: "220px 1fr" }, gap: 2.5 }}>
          {page.showFilters !== false ? (
            <Box sx={{ bgcolor: surface, borderRadius: 2.5, p: 2, alignSelf: "start" }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <FilterListRoundedIcon fontSize="small" />
                <Typography fontWeight={900}>{page.filtersLabel || "Filtros"}</Typography>
              </Stack>
              <Stack spacing={1.2}>
                {page.showTypeFilter !== false ? <Chip label={page.typeFilterLabel || "Tipo"} variant="outlined" /> : null}
                {page.showCategoryFilter !== false ? <Chip label={page.categoryFilterLabel || "Categoría"} variant="outlined" /> : null}
                {page.showSubcategoryFilter !== false ? <Chip label={page.subcategoryFilterLabel || "Subcategoría"} variant="outlined" /> : null}
              </Stack>
            </Box>
          ) : null}

          <Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(3, minmax(0,1fr))" }, gap: `${Number(cards.gap) || 18}px` }}>
              {["Producto destacado", "Servicio principal", "Nueva colección", "Más vendido", "Producto especial", "Servicio premium"].map((name, index) => (
                <Box
                  key={name}
                  sx={{
                    overflow: "hidden",
                    borderRadius: `${Number(cards.radius) || 16}px`,
                    bgcolor: cardBackground,
                    border: cards.borderColor ? `1px solid ${cards.borderColor}` : "1px solid rgba(25,36,47,.08)",
                  }}
                >
                  {cards.showImage !== false ? <Box sx={{ height: 112, bgcolor: "rgba(25,36,47,.10)" }} /> : null}
                  <Box sx={{ p: 1.5 }}>
                    {cards.showCategory !== false ? (
                      <Typography variant="caption" sx={{ color: cards.categoryColor || theme.primaryColor || "#F35A3A", fontWeight: 800 }}>
                        Categoría
                      </Typography>
                    ) : null}
                    {cards.showTitle !== false ? (
                      <Typography sx={{ mt: 0.3, fontWeight: 900, color: cards.titleColor || textColor }}>
                        {name}
                      </Typography>
                    ) : null}
                    {cards.showPrice !== false ? (
                      <Typography sx={{ mt: 1, fontWeight: 900, color: cards.priceColor || theme.secondaryColor || "#19242F" }}>
                        $ {index % 2 ? "25.000" : "18.500"}
                      </Typography>
                    ) : null}
                    {cards.showButton !== false ? (
                      <Button size="small" variant="outlined" fullWidth sx={{ mt: 1.2 }}>
                        {cards.ctaLabel || "Ver detalle"}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
            {page.paginationMode === "loadMore" ? (
              <Button variant="outlined" sx={{ display: "flex", mx: "auto", mt: 3 }}>
                {page.loadMoreLabel || "Cargar más"}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function ProductsPageEditor({ form, device, saving = false, onCommit }) {
  const page = pageValue(form);
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productsPage.${key}`, value), [onCommit]);

  return (
    <PageEditorShell
      title="Página de productos"
      description="Edita /productos sin duplicar la apariencia compartida de catalog.cards."
      device={device}
      saving={saving}
      preview={<ProductsPagePreview form={form} />}
    >
      <EditorGroup title="Encabezado y textos">
        <TextSetting label="Eyebrow" value={page.eyebrow} onChange={(value) => set("eyebrow", value)} />
        <TextSetting label="Título general" value={page.title} onChange={(value) => set("title", value)} helperText="Vacío = usar el nombre del catálogo de la navegación." />
        <TextSetting label="Título para productos" value={page.productsTitle} onChange={(value) => set("productsTitle", value)} />
        <TextSetting label="Título para servicios" value={page.servicesTitle} onChange={(value) => set("servicesTitle", value)} />
        <TextSetting label="Título para híbridos" value={page.hybridTitle} onChange={(value) => set("hybridTitle", value)} />
        <TextSetting label="Subtítulo" value={page.subtitle} onChange={(value) => set("subtitle", value)} multiline />
        <TextSetting label="Texto de carga" value={page.loadingText} onChange={(value) => set("loadingText", value)} />
        <TextSetting label="Resultado (singular)" value={page.resultSingular} onChange={(value) => set("resultSingular", value)} />
        <TextSetting label="Resultados (plural)" value={page.resultPlural} onChange={(value) => set("resultPlural", value)} />
      </EditorGroup>

      <EditorGroup title="Búsqueda y filtros">
        <ToggleSetting label="Mostrar buscador" checked={page.showSearch} onChange={(value) => set("showSearch", value)} />
        <TextSetting label="Placeholder del buscador" value={page.searchPlaceholder} onChange={(value) => set("searchPlaceholder", value)} />
        <ToggleSetting label="Mostrar filtros" checked={page.showFilters} onChange={(value) => set("showFilters", value)} />
        <ToggleSetting label="Filtro por tipo" checked={page.showTypeFilter} onChange={(value) => set("showTypeFilter", value)} />
        <ToggleSetting label="Filtro por categoría" checked={page.showCategoryFilter} onChange={(value) => set("showCategoryFilter", value)} />
        <ToggleSetting label="Filtro por subcategoría" checked={page.showSubcategoryFilter} onChange={(value) => set("showSubcategoryFilter", value)} />
        <Divider />
        <TextSetting label="Título de filtros" value={page.filtersLabel} onChange={(value) => set("filtersLabel", value)} />
        <TextSetting label="Etiqueta de tipo" value={page.typeFilterLabel} onChange={(value) => set("typeFilterLabel", value)} />
        <TextSetting label="Etiqueta de categoría" value={page.categoryFilterLabel} onChange={(value) => set("categoryFilterLabel", value)} />
        <TextSetting label="Etiqueta de subcategoría" value={page.subcategoryFilterLabel} onChange={(value) => set("subcategoryFilterLabel", value)} />
        <TextSetting label="Todos los tipos" value={page.allTypesLabel} onChange={(value) => set("allTypesLabel", value)} />
        <TextSetting label="Todas las categorías" value={page.allCategoriesLabel} onChange={(value) => set("allCategoriesLabel", value)} />
        <TextSetting label="Todas las subcategorías" value={page.allSubcategoriesLabel} onChange={(value) => set("allSubcategoriesLabel", value)} />
        <TextSetting label="Limpiar filtros" value={page.resetLabel} onChange={(value) => set("resetLabel", value)} />
        <TextSetting label="Aplicar filtros" value={page.applyFiltersLabel} onChange={(value) => set("applyFiltersLabel", value)} />
      </EditorGroup>

      <EditorGroup title="Resultados y orden">
        <SelectSetting
          label="Paginación"
          value={page.paginationMode || "all"}
          onChange={(value) => set("paginationMode", value)}
          options={[
            { value: "all", label: "Mostrar todos (compatibilidad)" },
            { value: "loadMore", label: "Cargar más" },
          ]}
        />
        <NumberSetting label="Resultados por bloque" value={page.pageSize} min={12} max={60} onChange={(value) => set("pageSize", value)} helperText="Se usa cuando el modo es Cargar más." />
        <SelectSetting
          label="Ordenar por"
          value={page.sortBy || "createdAt"}
          onChange={(value) => set("sortBy", value)}
          options={[
            { value: "createdAt", label: "Más recientes" },
            { value: "price", label: "Precio" },
            { value: "name", label: "Nombre" },
          ]}
        />
        <SelectSetting
          label="Dirección"
          value={page.sortDir || "desc"}
          onChange={(value) => set("sortDir", value)}
          options={[
            { value: "desc", label: "Descendente" },
            { value: "asc", label: "Ascendente" },
          ]}
        />
        <TextSetting label="Título sin resultados" value={page.emptyTitle} onChange={(value) => set("emptyTitle", value)} />
        <TextSetting label="Texto sin resultados" value={page.emptyText} onChange={(value) => set("emptyText", value)} multiline />
        <TextSetting label="Botón sin resultados" value={page.emptyResetLabel} onChange={(value) => set("emptyResetLabel", value)} />
        <TextSetting label="Botón cargar más" value={page.loadMoreLabel} onChange={(value) => set("loadMoreLabel", value)} />
      </EditorGroup>

      <EditorGroup title="Colores de la página" description="Las cards siguen usando catalog.cards y no se duplican aquí.">
        <ColorSetting label="Fondo de página" value={page.backgroundColor} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} onChange={(value) => set("mutedTextColor", value)} />
        <ColorSetting label="Fondo del encabezado" value={page.headerBackgroundColor} onChange={(value) => set("headerBackgroundColor", value)} />
        <ColorSetting label="Texto del encabezado" value={page.headerTextColor} onChange={(value) => set("headerTextColor", value)} />
      </EditorGroup>
    </PageEditorShell>
  );
}
